'use client';

import { forwardRef } from 'react';
import { Checkbox as MantineCheckbox } from '@mantine/core';
import type {
  CheckboxCardProps as MantineCheckboxCardProps,
  CheckboxGroupProps as MantineCheckboxGroupProps,
  CheckboxProps as MantineCheckboxProps,
} from '@mantine/core';

import type { LockedProps } from '../types';

export type CheckboxProps = Omit<MantineCheckboxProps, LockedProps>;

export type CheckboxGroupProps = Omit<MantineCheckboxGroupProps, LockedProps>;

export type CheckboxCardProps = Omit<MantineCheckboxCardProps, LockedProps>;

const CheckboxRoot = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  return <MantineCheckbox ref={ref} {...props} />;
});

CheckboxRoot.displayName = 'Checkbox';

const CheckboxGroup = (props: CheckboxGroupProps) => {
  return <MantineCheckbox.Group {...props} />;
};

CheckboxGroup.displayName = 'Checkbox.Group';

const CheckboxCard = forwardRef<HTMLButtonElement, CheckboxCardProps>((props, ref) => (
  <MantineCheckbox.Card ref={ref} {...props} />
));

CheckboxCard.displayName = 'Checkbox.Card';

type CheckboxRootComponent = typeof CheckboxRoot;

interface CheckboxCompound extends CheckboxRootComponent {
  /**
   * Group wrapper for managing checkbox collections with shared value state.
   *
   * @example
   * <Checkbox.Group value={selected} onChange={setSelected}>
   *   <Checkbox value="a" label="A" />
   * </Checkbox.Group>
   */
  Group: typeof CheckboxGroup;
  /**
   * Card-style clickable checkbox option. Use as a child of `Checkbox.Group`; render the
   * label/content as children.
   *
   * @example
   * <Checkbox.Group value={value} onChange={setValue}>
   *   <Checkbox.Card value="a" radius="md" px="sm" py="xs">
   *     <Text>Option A</Text>
   *   </Checkbox.Card>
   * </Checkbox.Group>
   */
  Card: typeof CheckboxCard;
}

/**
 * ### Checkbox
 *
 * @description
 * Checkbox input with locked visual styling. Use `Checkbox.Group` for grouped values.
 *
 * @example
 * <Checkbox checked={checked} onChange={handleChange} label="Receive updates" />
 * <Checkbox.Group value={selected} onChange={setSelected}>
 *   <Checkbox value="a" label="A" />
 * </Checkbox.Group>
 *
 * @param props - Locked checkbox props without direct styling escape hatches.
 * @returns Compound checkbox component (`Checkbox` and `Checkbox.Group`).
 */
export const Checkbox: CheckboxCompound = Object.assign(CheckboxRoot, {
  Group: CheckboxGroup,
  Card: CheckboxCard,
});
